import React, { useEffect, useMemo, useRef, useState } from "react";
import { Info } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchCategories } from "../../../api/categoryService";
import {
  checkImageProposalDuplicate,
  createImageProposal,
  fetchImageProposals,
} from "../../../api/communityService";
import { useAuth } from "../../../context/AuthContext";
import { useProfileHub } from "../ProfileHubContext";
import ProfileSectionCard from "../components/ProfileSectionCard";

const MIN_GAMES_FOR_PROPOSAL = 25;

const STATUS_LABELS = {
  pending: "En attente admin",
  voting: "En vote",
  integrated: "Intégrée au quiz",
  admin_rejected: "Refusee admin",
  vote_rejected: "Rejetée après vote",
};

function RulesPopover({ rules }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!rules || !rules.trim()) return null;

  const lines = rules.split("\n").map((l) => l.trim()).filter(Boolean);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen((v) => !v)}
        className="flex h-7 w-7 items-center justify-center rounded-full text-sky-400 transition hover:bg-sky-100 hover:text-sky-600"
        title="Règles de proposition"
      >
        <Info className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-2 w-72 rounded-2xl border border-sky-100 bg-white p-4 shadow-xl">
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-sky-600">
            Règles de proposition d'image
          </div>
          <ul className="space-y-1.5">
            {lines.map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" />
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function ProfileImageProposalsSection() {
  const { isAuthenticated, user } = useAuth();
  const profileHub = useProfileHub();
  const currentUserId = user?.user_id || user?.id;
  const isPrivileged = Boolean(
    user?.is_staff || user?.is_superuser || user?.is_moderator
  );
  const gamesByCategorySlug = useMemo(() => {
    const map = {};
    const scores = profileHub?.profileIdentity?.category_scores || [];
    scores.forEach((entry) => {
      map[entry.category] = entry.games_played || 0;
    });
    return map;
  }, [profileHub?.profileIdentity?.category_scores]);
  const [categories, setCategories] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [answer, setAnswer] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);

    Promise.all([
      fetchCategories(controller.signal),
      fetchImageProposals(controller.signal),
    ])
      .then(([categoryData, proposalData]) => {
        setCategories(Array.isArray(categoryData) ? categoryData : []);
        setProposals(
          Array.isArray(proposalData?.proposals) ? proposalData.proposals : []
        );
      })
      .catch(() => {
        setError("Chargement des propositions impossible.");
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [isAuthenticated]);

  const myProposals = useMemo(
    () =>
      proposals.filter(
        (proposal) => String(proposal.submitted_by_id) === String(currentUserId)
      ),
    [proposals, currentUserId]
  );
  const selectedCategory = useMemo(
    () =>
      categories.find((category) => String(category.id) === String(categoryId)),
    [categories, categoryId]
  );

  useEffect(() => {
    const trimmedAnswer = answer.trim();
    if (!isAuthenticated || !categoryId || trimmedAnswer.length < 2) {
      setDuplicateCheck(null);
      setIsCheckingDuplicate(false);
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      setIsCheckingDuplicate(true);
      checkImageProposalDuplicate(
        { categoryId, answer: trimmedAnswer },
        controller.signal
      )
        .then((data) => {
          setDuplicateCheck(data);
        })
        .catch((checkError) => {
          if (checkError?.name !== "AbortError") {
            setDuplicateCheck({
              status: "unknown",
              message: "Verification des doublons indisponible pour le moment.",
              matches: [],
            });
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsCheckingDuplicate(false);
          }
        });
    }, 450);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [answer, categoryId, isAuthenticated]);

  const submitProposal = async () => {
    if (!categoryId || !answer.trim() || !sourceUrl.trim() || !file) {
      setError("Catégorie, image, réponse et source sont requis.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setMessage("");

    try {
      const response = await createImageProposal({
        categoryId,
        answer: answer.trim(),
        sourceUrl: sourceUrl.trim(),
        file,
      });
      setProposals((current) => [response.proposal, ...current]);
      setCategoryId("");
      setAnswer("");
      setSourceUrl("");
      setFile(null);
      setDuplicateCheck(null);
      setMessage("Proposition envoyée. Elle attend la validation admin.");
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Impossible d'envoyer la proposition."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <ProfileSectionCard
        title="Propositions d'images"
        subtitle="Reserve aux joueurs inscrits et connectes."
      >
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-500">
          Connecte-toi pour proposer une image au quiz.
          <Link to="/authentification#connexion" className="ml-2 font-semibold text-sky-700">
            Se connecter
          </Link>
        </div>
      </ProfileSectionCard>
    );
  }

  return (
    <div className="space-y-6">
      <ProfileSectionCard
        title="Proposer une image"
        subtitle={`Il faut avoir joué au moins ${MIN_GAMES_FOR_PROPOSAL} parties dans une catégorie pour pouvoir y proposer une image. La proposition passe d'abord par une validation admin, puis par un vote public de 5 jours.`}
      >
        <div
          id="proposition-image"
          className="mb-4 flex flex-col gap-3 rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between"
        >
          <span>
            Les propositions approuvées par l'équipe de modération sont regroupées sur la page de vote publique.
          </span>
          <Link
            to="/suggestions-des-joueurs/"
            className="inline-flex rounded-full bg-white px-4 py-2 text-sm font-semibold text-sky-700 shadow-sm transition hover:bg-sky-100"
          >
            Voir les propositions des joueurs
          </Link>
        </div>

        {error ? (
          <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        {message ? (
          <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {message}
          </div>
        ) : null}

        {!selectedCategory ? (
          <div className="space-y-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                Etape 1
              </div>
              <h3 className="mt-1 text-lg font-semibold text-slate-950">
                Choisis une catégorie
              </h3>
            </div>

            {isLoading ? (
              <div className="text-sm text-slate-500">
                Chargement des catégories...
              </div>
            ) : categories.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                Aucune catégorie active disponible pour le moment.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {categories.map((category) => {
                  const gamesPlayed = gamesByCategorySlug[category.slug] || 0;
                  const isLocked = !isPrivileged && gamesPlayed < MIN_GAMES_FOR_PROPOSAL;

                  return (
                    <button
                      key={category.id || category.slug}
                      type="button"
                      disabled={isLocked}
                      onClick={() => {
                        if (isLocked) return;
                        setCategoryId(String(category.id));
                        setError("");
                        setMessage("");
                      }}
                      title={
                        isLocked
                          ? `Joue ${MIN_GAMES_FOR_PROPOSAL} parties dans cette catégorie pour proposer une image (${gamesPlayed}/${MIN_GAMES_FOR_PROPOSAL})`
                          : undefined
                      }
                      className={`rounded-2xl border px-4 py-4 text-left text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-sky-300 ${
                        isLocked
                          ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400"
                          : "border-slate-200 bg-white text-slate-800 hover:border-sky-300 hover:bg-sky-50"
                      }`}
                    >
                      <div>{category.name}</div>
                      {isLocked ? (
                        <div className="mt-1 text-xs font-medium text-slate-400">
                          {gamesPlayed}/{MIN_GAMES_FOR_PROPOSAL} parties jouees
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-col gap-3 rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">
                  Etape 2
                </div>
                <div className="mt-1 text-sm text-slate-600">
                  Catégorie choisie
                </div>
                <div className="text-lg font-semibold text-slate-950">
                  {selectedCategory.name}
                </div>
              </div>
              <div className="flex items-center gap-2 self-start sm:self-center">
                <RulesPopover rules={selectedCategory.proposal_rules} />
                <button
                  type="button"
                  onClick={() => {
                    setCategoryId("");
                    setAnswer("");
                    setSourceUrl("");
                    setFile(null);
                    setDuplicateCheck(null);
                    setError("");
                    setMessage("");
                  }}
                  className="rounded-full border border-sky-200 bg-white px-4 py-2 text-sm font-semibold text-sky-700 transition hover:bg-sky-100"
                >
                  Changer
                </button>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
              <label className="space-y-2">
                <span className="text-sm font-semibold text-slate-700">
                  Réponse
                </span>
                <input
                  type="text"
                  value={answer}
                  onChange={(event) => {
                    setAnswer(event.target.value);
                    setMessage("");
                  }}
                  placeholder="Ex : Taylor Swift"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
                />
                {isCheckingDuplicate ? (
                  <p className="text-xs font-semibold text-slate-500">
                    Verification des doublons...
                  </p>
                ) : duplicateCheck ? (
                  <div
                    className={`rounded-2xl border px-4 py-3 text-xs font-semibold ${
                      duplicateCheck.status === "in_game"
                        ? "border-rose-200 bg-rose-50 text-rose-700"
                        : duplicateCheck.status === "proposed"
                          ? "border-amber-200 bg-amber-50 text-amber-700"
                          : duplicateCheck.status === "available"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-slate-50 text-slate-600"
                    }`}
                  >
                    {duplicateCheck.message}
                  </div>
                ) : null}
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-slate-700">
                  Source / lien de l'image
                </span>
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={(event) => setSourceUrl(event.target.value)}
                  placeholder="https://exemple.com/source-image"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
                />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-slate-700">
                  Image
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600"
                />
              </label>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={submitProposal}
                disabled={isSubmitting}
                className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? "Envoi..." : "Envoyer la proposition"}
              </button>
            </div>
          </div>
        )}
      </ProfileSectionCard>

      <ProfileSectionCard
        title="Mes propositions"
        subtitle="Suivi des propositions envoyées."
      >
        {myProposals.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
            Aucune proposition envoyée.
          </div>
        ) : (
          <div className="space-y-3">
            {myProposals.map((proposal) => (
              <div
                key={proposal.id}
                className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <div className="font-semibold text-slate-950">
                    {proposal.answer}
                  </div>
                  <div className="text-sm text-slate-500">
                    {proposal.category_name} - {STATUS_LABELS[proposal.status] || proposal.status}
                  </div>
                  {proposal.source_url ? (
                    <a
                      href={proposal.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 inline-flex text-xs font-semibold text-sky-700 hover:text-sky-900"
                    >
                      Source
                    </a>
                  ) : null}
                </div>
                <div className="text-sm font-semibold text-slate-600">
                  Oui {proposal.upvotes} / Non {proposal.downvotes}
                </div>
              </div>
            ))}
          </div>
        )}
      </ProfileSectionCard>
    </div>
  );
}
